import { ArrowRightOutlined, EyeOutlined } from '@ant-design/icons'
import type { Member } from '@di-devs/member-feature-plugin/shared'
import { useBetterFeatureClient } from '@di-devs/utils-fe'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
	Alert,
	Button,
	Col,
	DatePicker,
	Descriptions,
	Divider,
	Form,
	Image,
	Input,
	InputNumber,
	Modal,
	Radio,
	Row,
	Space,
	Tag,
	message,
} from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useNavigate } from 'react-router'
import styled from 'styled-components'
import type { FeatureClient } from '@/config/client'
import { useListCoupons } from '@/queries'
import { API, COMMONS, tsRestClient } from '@/utils'

const CustomDatePicker = styled(DatePicker)`
	.ant-picker-input > input {
		text-align: center;
	}
`
const MemberPointUpdateModal = (props: {
	publicSettings?: any
	confirmMember?: any
	isMemberVisit?: boolean
	isMemberRenewConfirmModalVisible?: any
	hideMemberRenewConfirmModal?: any
	memberEditMutation?: any
	currentMember?: Member | null
	renewConfirmPickerValue?: any
	setRenewConfirmPickerValue?: any
}) => {
	const reactFeatureClient = useBetterFeatureClient<FeatureClient>()
	const {
		isMemberVisit = false,
		isMemberRenewConfirmModalVisible,
		hideMemberRenewConfirmModal,
		confirmMember,
		publicSettings,
		memberEditMutation,
		setRenewConfirmPickerValue,
	} = props

	const [memberPointForm] = Form.useForm()
	const [memberRenewConfirmForm] = Form.useForm()
	const watchedActiveUntil = Form.useWatch('activeUntil', memberRenewConfirmForm)
	const queryClient: any = useQueryClient()
	const [isRenewConfirmPickerOpen, setIsRenewConfirmPickerOpen] = useState(false)
	const navigate = useNavigate()
	const { data: companionCouponsResponse } = useListCoupons(
		{
			memberCode: confirmMember?.memberCode,
			status: 'unused',
			page: 1,
			limit: 100,
		},
		{
			enabled:
				Boolean(isMemberVisit) &&
				Boolean(isMemberRenewConfirmModalVisible) &&
				Boolean(confirmMember?.memberCode),
		},
	)

	const companionTicketsAvailable =
		companionCouponsResponse?.body?.data?.coupons
			?.filter((coupon) => coupon.couponMaster?.couponType === 'companion_ticket')
			.reduce((total, coupon) => {
				const { useLimitType, maxUsesPerMember } = coupon.couponMaster || {}
				if (useLimitType === 'unlimited') return total + 999
				const maxUses = useLimitType === 'once' ? 1 : (maxUsesPerMember ?? 1)
				return total + Math.max(0, maxUses - coupon.usedCount)
			}, 0) ?? 0

	const { data: memberPaymentsData } = useQuery({
		queryKey: ['memberPayments', confirmMember?.memberId],
		queryFn: () => API.GET_MEMBER_PAYMENTS(confirmMember?.memberId),
		enabled:
			!isMemberVisit &&
			Boolean(isMemberRenewConfirmModalVisible) &&
			Boolean(confirmMember?.memberId),
	})

	const recentPayment = useMemo(() => {
		const payments = memberPaymentsData?.data?.data
		if (!payments?.length) return null
		const thirtyMinAgo = dayjs().subtract(30, 'minutes')
		const found = payments.find(
			(p: any) => dayjs(p.completedAt).isAfter(thirtyMinAgo),
		)
		return found || null
	}, [memberPaymentsData])

	const memberRenewMutation = useMutation({
		mutationFn: (data: any) => API.ADMIN_UPDATE_MEMBERSHIP_MANUAL(data),
		onSuccess: () => {
			message.success(COMMONS.SUCCESS_UPDATE_MSG)
			hideMemberRenewConfirmModal()
			queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_MEMBERS)
		},
		onError: (error: FetchError) => {
			if (error?.response?.status === COMMONS.RESPONSE_PERMISSION_ERROR) {
				navigate(COMMONS.PERMISSION_ERROR_ROUTE)
			} else if (error?.response?.status === COMMONS.RESPONSE_SESSION_ERROR) {
				message.warning(COMMONS.ERROR_SESSION_MSG)
				navigate(COMMONS.ADMIN_LOGIN_ROUTE)
			} else if (error?.response?.status === COMMONS.RESPONSE_SYSTEM_ERROR) {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			} else {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			}
		},
	})

	const recordVisitMutation = useMutation({
		mutationFn: (data: any) => API.ADMIN_CHECK_MEMBER(data),
		onSuccess: () => {
			message.success(COMMONS.SUCCESS_UPDATE_MSG)
			hideMemberRenewConfirmModal()
			queryClient.invalidateQueries(API.QUERY_KEY_ADMIN_MEMBERS)
		},
		onError: (error: any) => {
			if (error?.response?.data?.message) {
				message.error(error.response.data.message)
			} else {
				message.error(COMMONS.ERROR_SYSTEM_MSG)
			}
		},
	})
	const handleMemberPointUpdate = (data: {
		kakeruPointIsAdd: boolean
		kakeruPoint: string
		companionCount?: number
	}) => {
		const paramData = {
			memberCode: confirmMember?.memberCode,
			isMemberVisit: true,
			companionCount: data.companionCount || 0,
			additionalPoints: Number(data.kakeruPoint) || 0,
		}

		recordVisitMutation.mutate(paramData)
	}

	const handleMemberRenew = (data: any) => {
		const isPaidOption = data.paymentAction === 'renew' || data.paymentAction === 'new'

		if (isPaidOption && recentPayment) {
			Modal.confirm({
				title: '支払い記録があります',
				content: `30分以内に ¥${recentPayment.amount.toLocaleString()} の支払いが記録されています。会員履歴に二重に支払い記録が残ります。有効期限のみを変更する場合は「日付のみ変更（支払なし）」を選択してください。それでも続行しますか？`,
				okText: '続行する',
				cancelText: 'キャンセル',
				onOk: () => {
					const paramData = {
						memberId: confirmMember?.memberId,
						activeUntil: dayjs(data.activeUntil).format('YYYY-MM-DD'),
						paymentAction: data.paymentAction,
					}
					memberRenewMutation.mutate(paramData)
				},
			})
			return
		}

		const paramData = {
			memberId: confirmMember?.memberId,
			activeUntil: dayjs(data.activeUntil).format('YYYY-MM-DD'),
			paymentAction: data.paymentAction,
		}

		memberRenewMutation.mutate(paramData)
	}
	useEffect(() => {
		if (isMemberRenewConfirmModalVisible && confirmMember?.memberId) {
			memberPointForm.setFieldsValue({
				kakeruPoint: 0, // confirmMember?.kakeruPoint
				companionCount: 0,
			})

			let defaultPaymentAction = 'renew'
			if ((confirmMember as any)?.membershipStatus === 'provisional') {
				defaultPaymentAction = 'new'
			} else if (
				confirmMember?.activeUntil &&
				dayjs(confirmMember.activeUntil).isAfter(dayjs(), 'day')
			) {
				defaultPaymentAction = 'none'
			}

			if (!isMemberVisit && recentPayment) {
				defaultPaymentAction = 'none'
			}

			const baseDate =
				confirmMember?.activeUntil &&
				!dayjs(confirmMember.activeUntil).isBefore(dayjs(), 'day')
					? dayjs(confirmMember.activeUntil)
					: dayjs()

			memberRenewConfirmForm.setFieldsValue({
				activeUntil: baseDate.add(1, 'year').endOf('month'),
				paymentAction: defaultPaymentAction,
			})
		}
	}, [confirmMember, memberPointForm, isMemberRenewConfirmModalVisible, memberRenewConfirmForm, isMemberVisit, recentPayment])

	return (
		<>
			<Modal
				title={`${isMemberVisit ? '来店記録' : '会員更新'}`}
				width={750}
				open={isMemberRenewConfirmModalVisible}
				onCancel={hideMemberRenewConfirmModal}
				footer={null}
				destroyOnClose
				centered
				styles={{
					body: {
						maxHeight: '80vh',
						overflowY: 'auto',
						overflowX: 'hidden',
					},
				}}
			>
				<Row>
					<Col span={24}>
						<div className='text-center mb-4'>
							{confirmMember?.picUrl ? (
								<Image
									preview={{
										mask: <EyeOutlined rev={undefined} />,
										src: confirmMember.picUrl,
										maskClassName: 'rounded-full',
									}}
									width={100}
									height={100}
									className='rounded-full'
									src={
										confirmMember.picUrl
											? confirmMember.picUrl.includes('randomuser')
												? confirmMember.picUrl
												: `${confirmMember.picUrl}/large`
											: ''
									}
									fallback='/no-image.png'
								/>
							) : (
								<Image
									src='/no-image.png'
									width={100}
									height={100}
									className='rounded-full'
									preview={{
										mask: <EyeOutlined rev={undefined} />,
										src: '/no-image.png',
										maskClassName: 'rounded-full',
									}}
								/>
							)}
						</div>
						<p className='text-center font-bold text-xl'>
							{/* {confirmMember?.lastName || 'ー'} {confirmMember?.firstName || 'ー'}様 */}
							{(confirmMember as any)?.memberAttributeId1 ||
								confirmMember?.displayName ||
								'ー'}
						</p>
						{/* <p className="text-center text-sm">
							（{confirmMember?.lastNameKana || 'ー'} {confirmMember?.firstNameKana || 'ー'}）
						</p> */}
						<Divider>会員情報</Divider>
						<Descriptions column={1} layout='horizontal' bordered>
							<Descriptions.Item label='ID' className='text-center'>
								{confirmMember?.memberId || 'ー'}
							</Descriptions.Item>
							<Descriptions.Item label='会員コード' className='text-center'>
								{confirmMember?.memberCode || 'ー'}
							</Descriptions.Item>
							<Descriptions.Item label='会員登録日' className='text-center'>
								{confirmMember?.memberSince
									? dayjs(confirmMember.memberSince).format('YYYY年M月D日')
									: 'ー'}
							</Descriptions.Item>
							<Descriptions.Item label='来館回数' className='text-center'>
								{confirmMember?.countVisit || '0'}回
							</Descriptions.Item>
							<Descriptions.Item label='最終来館日' className='text-center'>
								{confirmMember?.visits && confirmMember?.visits.length > 0
									? dayjs(confirmMember.lastVisit).format('YYYY年M月D日')
									: 'ー'}
							</Descriptions.Item>
							<Descriptions.Item label='有効期限' className='text-center'>
								{confirmMember?.activeUntil ? (
									<Tag
										color={
											dayjs(confirmMember.activeUntil).isAfter(dayjs(), 'day')
												? 'green'
												: 'red'
										}
										className='mr-0'
									>
										{dayjs(confirmMember.activeUntil).format('YYYY年M月D日')}
									</Tag>
								) : (
									'ー'
								)}
							</Descriptions.Item>
							<Descriptions.Item label='ポイント' className='text-center'>
								<span
									className='font-bold'
									style={{ color: publicSettings?.PRIMARY_COLOR }}
								>
									{confirmMember?.kakeruPoint ?? 'ー'}pt
								</span>
							</Descriptions.Item>
						</Descriptions>
					</Col>

					{isMemberVisit ? (
						<>
							<Form
								form={memberPointForm}
								onFinish={handleMemberPointUpdate}
								className='w-full'
								initialValues={{
									kakeruPointIsAdd: true,
									memberId: confirmMember?.memberId,
									memberVisitId: confirmMember?.memberVisitId,
								}}
								layout='vertical'
								preserve={false}
								size='large'
							>
								<Divider>ポイント変更</Divider>
								<Col>
									<Form.Item name='memberId' hidden>
										<Input />
									</Form.Item>
									<Form.Item hidden name='memberVisitId'>
										<Input />
									</Form.Item>
								</Col>
								<Col span={24}>
									<div className='mb-3 text-right text-sm'>
										同伴者チケット利用可能: {companionTicketsAvailable}枚
									</div>
									<Form.Item
										label='同伴者数（チケット利用）'
										name='companionCount'
										rules={[
											{
												validator: (_, value) => {
													if ((value || 0) <= companionTicketsAvailable) {
														return Promise.resolve()
													}
													return Promise.reject(
														new Error(
															`同伴者チケット利用可能枚数（${companionTicketsAvailable}枚）を超えています`,
														),
													)
												},
											},
										]}
									>
										<InputNumber
											className='w-full'
											inputMode='numeric'
											min={0}
											max={companionTicketsAvailable}
										/>
									</Form.Item>
									<Form.Item label='販売会・イベントP' name='kakeruPoint'>
										<InputNumber
											className='w-full'
											inputMode='numeric'
											min={0}
										/>
									</Form.Item>
									{/* kakeruPointIsAdd is always true - admins can only add points */}
									<Form.Item name='kakeruPointIsAdd' hidden>
										<Input />
									</Form.Item>
								</Col>
								<Divider />
								<Col span={24} className='text-center'>
									<Button
										type='primary'
										htmlType='submit'
										size='large'
										className='px-12'
										loading={recordVisitMutation.isLoading}
									>
										変更
									</Button>
								</Col>
							</Form>
						</>
					) : (
						<>
							<Divider>有効期間更新</Divider>
							<Col span={24}>
								{(() => {
									const today = dayjs()
									const baseDate =
										confirmMember?.activeUntil &&
										!dayjs(confirmMember.activeUntil, 'YYYY-MM-DD').isBefore(today, 'day')
											? dayjs(confirmMember.activeUntil, 'YYYY-MM-DD')
											: today

									const expectedDate = baseDate.add(1, 'year').endOf('month')

									const getDynamicColorStyle = () => {
										if (!watchedActiveUntil) return 'bg-gray-100 text-gray-600 border-gray-300'
										const target = dayjs(watchedActiveUntil)
										if (!target.isValid()) return 'bg-gray-100 text-gray-600 border-gray-300'

										// Exact match with expected 1-year end-of-month date -> Bright Solid Green
										if (target.isSame(expectedDate, 'day')) {
											return 'bg-emerald-600 text-white font-black shadow-xs'
										}

										// Past date before today -> Bright Red
										if (target.isBefore(today, 'day')) {
											return 'bg-red-600 text-white font-black shadow-xs'
										}

										// Shorter than expected -> Light Red Warning
										if (target.isBefore(expectedDate, 'day')) {
											return 'bg-rose-100 text-rose-900 border-2 border-rose-400 font-extrabold'
										}

										// Longer than expected -> Light Amber Warning
										return 'bg-amber-100 text-amber-900 border-2 border-amber-400 font-extrabold'
									}

									const getDetailedBreakdown = () => {
										if (!watchedActiveUntil) return 'ー'
										const target = dayjs(watchedActiveUntil)
										if (!target.isValid()) return 'ー'

										const totalDays = target.diff(baseDate, 'day')
										if (totalDays < 0) return `期限切れ (過去 ${Math.abs(totalDays)}日)`

										const months = target.diff(baseDate, 'month')
										const years = Math.floor(months / 12)
										const remMonths = months % 12
										const remDays = target.diff(baseDate.add(months, 'month'), 'day')

										let periodLabel = ''
										if (years > 0) periodLabel += `${years}年`
										if (remMonths > 0 || years > 0) periodLabel += `${remMonths}ヶ月`
										if (remDays > 0) periodLabel += `${remDays}日`

										return `(${years > 0 ? `${years}年 / ` : ''}${periodLabel || '0日'} / 計${totalDays}日)`
									}

									return (
										<div className='mb-6 p-4 bg-white rounded-xl border border-gray-200 shadow-sm'>
											<div className='flex flex-wrap items-center justify-between gap-y-4 gap-x-2 sm:gap-x-4'>
												<div className='flex flex-col items-start'>
													<span className='text-xs font-bold text-gray-500 mb-1.5 whitespace-nowrap'>現在の有効期限</span>
													<div className='flex items-center gap-2 h-9'>
														{confirmMember?.activeUntil ? (
															<>
																<span className='text-base font-extrabold text-gray-900 whitespace-nowrap leading-none'>
																	{dayjs(confirmMember.activeUntil).format('YYYY年M月D日')}
																</span>
																{dayjs(confirmMember.activeUntil).isBefore(today, 'day') ? (
																	<span className='px-2.5 py-0.5 rounded-full text-xs font-bold bg-red-600 text-white shadow-2xs whitespace-nowrap'>
																		期限切れ
																	</span>
																) : (
																	<span className='px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-600 text-white shadow-2xs whitespace-nowrap'>
																		有効
																	</span>
																)}
															</>
														) : (
															<span className='text-sm text-gray-400 font-semibold whitespace-nowrap'>未設定</span>
														)}
													</div>
												</div>

												<div className='flex flex-col items-center justify-center px-1'>
													<div className='flex items-center justify-center w-full mb-1.5 h-4'>
														<ArrowRightOutlined className='text-slate-500 text-sm' />
													</div>
													<div className='flex items-center justify-center'>
														<div className='flex items-center justify-center px-3.5 py-1.5 bg-slate-100 rounded-lg border border-slate-300/80 shadow-2xs text-center shrink-0'>
															<span className='text-xs sm:text-base font-bold text-slate-800 whitespace-nowrap leading-none'>
																{getDetailedBreakdown()}
															</span>
														</div>
													</div>
												</div>

												<div className='flex flex-col items-start'>
													<span className='text-xs font-bold text-gray-500 mb-1.5 whitespace-nowrap'>更新後の有効期限</span>
													<div className='flex items-center'>
														<div className={`px-3.5 py-1.5 rounded-lg shadow-2xs transition-colors duration-200 flex items-center justify-center ${getDynamicColorStyle()}`}>
															<span className='text-base font-bold whitespace-nowrap leading-none'>
																{watchedActiveUntil
																	? dayjs(watchedActiveUntil).format('YYYY年M月D日')
																	: 'ー'}
															</span>
														</div>
													</div>
												</div>
											</div>
										</div>
									)
								})()}
								{!isMemberVisit && recentPayment && (
									<Alert
										type='warning'
										showIcon
										message={`30分以内に ¥${recentPayment.amount.toLocaleString()} の支払いが記録されています`}
										description='会員履歴に二重に支払いが残るため、有効期限のみ変更する場合は「日付のみ変更（支払なし）」を選択してください。'
										className='mb-4'
									/>
								)}
								<Form
									name='memberRenewConfirmForm'
									form={memberRenewConfirmForm}
									onFinish={handleMemberRenew}
									layout='vertical'
									preserve={false}
									size='large'
								>
									<Row justify='center'>
										<Col>
											<Form.Item
												name='paymentAction'
												label='更新内容'
												initialValue='renew'
												rules={[{ required: true, message: '必須です' }]}
											>
												<Radio.Group>
													<Space direction='vertical'>
														<Radio value='new'>
															新規/再入会 (5,000円)
														</Radio>
														<Radio value='renew'>更新 (3,000円)</Radio>
														<Radio value='none'>
															日付のみ変更 (支払なし)
															{!isMemberVisit && recentPayment && (
																<Tag
																	color='blue'
																	className='ml-2'
																	style={{ fontSize: '11px', lineHeight: '18px' }}
																>
																	推奨
																</Tag>
															)}
														</Radio>
													</Space>
												</Radio.Group>
											</Form.Item>
										</Col>
										<Col>
											<Form.Item name='memberId' hidden>
												<Input />
											</Form.Item>
										</Col>
										<Col>
											<Form.Item
												name='activeUntil'
												label='有効期限更新'
												rules={[
													{
														required: true,
														message: '必須です',
													},
												]}
											>
												{/* {isMobile ? (
													<Input
														placeholder="例：有効期限を選択してください"
														readOnly
														className="cursor-pointer"
														onPressEnter={(e) => e.preventDefault()}
														onClick={() => {
															setIsRenewConfirmPickerOpen(true)
														}}
													/>
												) : ( */}
												<CustomDatePicker
													style={{ width: '280px' }}
													inputReadOnly
												/>
												{/* )} */}
											</Form.Item>
											{/* <div className='text-center'>
												<Button
													size='small'
													onClick={() => {
														const targetDate = dayjs()
															.add(1, 'year')
															.endOf('month')

														memberRenewConfirmForm.setFieldValue(
															'activeUntil',
															targetDate,
														)
													}}
												>
													1年延長
												</Button>
											</div> */}
										</Col>
										<Col span={24} className='text-center'>
											<Button
												type='primary'
												htmlType='submit'
												loading={memberRenewMutation.isLoading}
												className='px-12'
											>
												更新
											</Button>
										</Col>
									</Row>
								</Form>
								{/* {isMobile ? (
									<DatePicker
										//@ts-ignore
										isOpen={isRenewConfirmPickerOpen}
										// value={renewConfirmPickerValue}
										theme="ios"
										confirmText="確定"
										cancelText="キャンセル"
										min={dayjs().toDate()}
										onSelect={(time) => {
											setIsRenewConfirmPickerOpen(false)

											memberRenewConfirmForm.setFieldsValue({
												activeUntil: dayjs(time).format('YYYY-MM-DD')
											})

											setRenewConfirmPickerValue(time)
										}}
										onCancel={() => {
											setIsRenewConfirmPickerOpen(false)
										}}
									/>
								) : (
									''
								)} */}
							</Col>
						</>
					)}
				</Row>
			</Modal>
		</>
	)
}

export default MemberPointUpdateModal
